import {Button, Space} from "antd";
import {
  StyledGetStartedWidgetModal,
  StyledGetStartedWidgetWrapper,
  StyledWidgetInfoBlock,
  StyledWidgetWrapper,
} from "../widget.style";
import {GetStartedWidgetItem} from "./single-tab";
import {OnboardingStep, useProfileStatus} from "../../../features/profile-status";
import {useGetProfileContext} from "../../../context/profile/profile-context";
import {BarsOutlined, CloseOutlined} from "@ant-design/icons";
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  getGoogleCalendarSettingsPath,
  IS_TWO_FACTOR_FEATURE_ENABLED,
} from "../../../services/config";
import {useGoogleCalendarConnection} from "../../../react-query-hooks/connect-calendar/useGoogleCalendarConnection";

type GetStartedWidgetProps = {
  setOpenOfferingCategoryModal: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * Returns remaining onboarding steps, treating Google Calendar connection
 * as complete when the calendar integration reports connected — even if
 * profile/status.calendarConnected lags behind.
 */
function getIncompleteGetStartedSteps({
  incompleteSteps,
  isGoogleCalendarConnected,
}: {
  incompleteSteps: OnboardingStep[];
  isGoogleCalendarConnected: boolean;
}): OnboardingStep[] {
  return incompleteSteps.filter((step) => {
    if (step === OnboardingStep.Calendar && isGoogleCalendarConnected) {
      return false;
    }
    return true;
  });
}

export function GetStartedWidget({setOpenOfferingCategoryModal}: GetStartedWidgetProps) {
  const {data: status} = useProfileStatus();
  const {isConnected: isGoogleCalendarConnected} = useGoogleCalendarConnection();
  const {state: profileState} = useGetProfileContext();
  // Treated as already done while the feature is off, so the step drops out of
  // the count and the widget hides itself exactly as it did before 2FA existed.
  const isTwoFactorEnabled = IS_TWO_FACTOR_FEATURE_ENABLED
    ? !!profileState.profileData.twoFactorMethod
    : true;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const incompleteSteps = status
    ? getIncompleteGetStartedSteps({
        incompleteSteps: status.incompleteSteps(),
        isGoogleCalendarConnected,
      })
    : [];
  const isCalendarConnected =
    isGoogleCalendarConnected || Boolean(status?.calendarConnected);

  if (!status || (incompleteSteps.length === 0 && isTwoFactorEnabled)) {
    return null;
  }

  const stepsLeft = incompleteSteps.length + (isTwoFactorEnabled ? 0 : 1);

  return (
    <StyledGetStartedWidgetWrapper>
      <div className="show-desktop">
        <DesktopGetStartedWidget
          hasOffering={status.hasOffering}
          isBankConnected={status.bankAccountConnected}
          isCalendarConnected={isCalendarConnected}
          isProfilePictureUploaded={status.profilePictureUploaded}
          isKycCompleted={status.kycVerified}
          isTwoFactorEnabled={isTwoFactorEnabled}
          stepsLeft={stepsLeft}
          closeModal={closeModal}
          setOpenOfferingCategoryModal={setOpenOfferingCategoryModal}
        />
      </div>
      <div className="show-mobile">
        <div
          style={{
            position: "fixed",
            top: "50%",
            right: "3rem",
            zIndex: 5,
          }}
        >
          <div
            style={{
              position: "relative",
            }}
          >
            <div
              style={{
                width: "2rem",
                height: "2rem",
                position: "absolute",
                zIndex: 103,
                backgroundColor: "#CF1322",
                borderRadius: "50%",
                color: "#F5F5F5",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "1.2rem",
                right: 0,
                top: "-10px",
              }}
            >
              {stepsLeft}
            </div>
            <Button
              style={{
                backgroundColor: "#F5F5F5",
                boxShadow: "0px 9px 28px 8px #0000001A",
                border: 0,
                padding: "1.2rem",
              }}
              size="small"
              onClick={showModal}
            >
              <BarsOutlined style={{fontSize: "2.4rem"}} />
            </Button>
          </div>
        </div>
        <StyledGetStartedWidgetModal
          footer={null}
          title={null}
          closable={false}
          visible={isModalOpen}
          maskClosable
          placement="bottom"
          onClose={closeModal}
        >
          <DesktopGetStartedWidget
            hasOffering={status.hasOffering}
            isBankConnected={status.bankAccountConnected}
            isCalendarConnected={isCalendarConnected}
            isProfilePictureUploaded={status.profilePictureUploaded}
            isKycCompleted={status.kycVerified}
            isTwoFactorEnabled={isTwoFactorEnabled}
            stepsLeft={stepsLeft}
            closeModal={closeModal}
            setOpenOfferingCategoryModal={setOpenOfferingCategoryModal}
            isMobile
          />
        </StyledGetStartedWidgetModal>
      </div>
    </StyledGetStartedWidgetWrapper>
  );
}

type DesktopGetStartedWidgetProps = {
  stepsLeft: number;
  isProfilePictureUploaded: boolean;
  isBankConnected: boolean;
  isCalendarConnected: boolean;
  hasOffering: boolean;
  isKycCompleted: boolean;
  isTwoFactorEnabled: boolean;
  closeModal: () => void;
  setOpenOfferingCategoryModal: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile?: boolean;
};

function DesktopGetStartedWidget({
  hasOffering,
  isBankConnected,
  isCalendarConnected,
  isProfilePictureUploaded,
  isKycCompleted,
  isTwoFactorEnabled,
  stepsLeft,
  isMobile,
  closeModal,
  setOpenOfferingCategoryModal,
}: DesktopGetStartedWidgetProps) {
  const navigate = useNavigate();
  return (
    <StyledWidgetWrapper
      style={{
        padding: isMobile ? "0" : "1.6rem",
        border: isMobile ? "none" : "1px solid #f0f0f0",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1.6rem",
        }}
      >
        <Space size={8} align="center">
          <h2 className="widget-heading">Get started with Coachli</h2>
          {!isMobile && (
            <div
              style={{
                backgroundColor: "#CF1322",
                height: "1.6rem",
                width: "1.6rem",
                borderRadius: "50%",
                color: "#F5F5F5",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "1.2rem",
              }}
            >
              {stepsLeft}
            </div>
          )}
        </Space>
        {isMobile && (
          <div
            onClick={closeModal}
            style={{
              padding: ".463rem .458rem",
            }}
          >
            <CloseOutlined
              style={{
                fontSize: "1.6rem",
                cursor: "pointer",
              }}
            />
          </div>
        )}
      </div>
      <div className="widget-card">
        <Space direction="vertical" size={12} style={{display: "flex"}}>
          <StyledWidgetInfoBlock style={{background: "#EBF3EC", color: "#193c11"}}>
            <p>Complete these simple steps to get the most out of your Coachli account</p>
          </StyledWidgetInfoBlock>
          <Space size={18} direction="vertical">
            <GetStartedWidgetItem
              handleClick={() => {
                navigate("/profile");
              }}
              isDone={isProfilePictureUploaded}
              number={1}
              text="Upload profile picture"
            />
            <GetStartedWidgetItem
              handleClick={() => {
                navigate("/payouts");
              }}
              isDone={isBankConnected}
              number={2}
              text="Connect your bank account"
            />
            <GetStartedWidgetItem
              handleClick={() => {
                navigate(getGoogleCalendarSettingsPath());
              }}
              isDone={isCalendarConnected}
              number={3}
              text="Connect your calendar"
            />
            <GetStartedWidgetItem
              handleClick={() => {
                closeModal();
                navigate("/new-offering");
              }}
              isDone={hasOffering}
              number={4}
              text="Create your first offering"
            />
            <GetStartedWidgetItem
              handleClick={() => {
                navigate("/settings?activeTab=kyc");
              }}
              isDone={isKycCompleted}
              number={5}
              text="Complete your KYC verification"
            />
            {IS_TWO_FACTOR_FEATURE_ENABLED && (
              <GetStartedWidgetItem
                handleClick={() => {
                  navigate("/settings?activeTab=account");
                }}
                isDone={isTwoFactorEnabled}
                number={6}
                text="Enable 2-factor authentication"
              />
            )}
          </Space>
        </Space>
      </div>
    </StyledWidgetWrapper>
  );
}
